import {
  SEARCH_DISTRICT_CODES_FOR_JP_USER,
  SEARCH_DISTRICT_CODES_FOR_JP_USER_INCLUDING_KO_HOSPITALS,
  SEARCH_DISTRICT_CODES_FOR_KO_USER,
} from '@/features/district-filter/district-filter.types'

export enum Locales {
  KO_KR = 'ko-KR',
  JA_JP = 'ja-JP',
  EN_US = 'en-US',
  TH_TH = 'th-TH',
  ZH_HANS_CN = 'zh-Hans-CN',
  ZH_HANT_TW = 'zh-Hant-TW',
}

export enum Countries {
  KR = 'kr',
  JP = 'jp',
  US = 'us',
  TH = 'th',
  CN = 'cn',
  TW = 'tw',
}

export enum CountryCodes {
  KR = 'KR',
  JP = 'JP',
  US = 'US',
  TH = 'TH',
  CN = 'CN',
  TW = 'TW',
}

export enum Languages {
  KO = 'ko',
  JA = 'ja',
  EN = 'en',
  TH = 'th',
  ZH_HANS = 'zh-Hans', // 중국어 간체
  ZH_HANT = 'zh-Hant', // 중국어 번체
}

export type PathnamePrefix = '' | `/${Countries}`

export const countryToLocale = (country: Countries): Locales => {
  const countryLocaleMap = new Map<Countries, Locales>([
    [Countries.KR, Locales.KO_KR],
    [Countries.JP, Locales.JA_JP],
    [Countries.US, Locales.EN_US],
    [Countries.TH, Locales.TH_TH],
    [Countries.CN, Locales.ZH_HANS_CN],
    [Countries.TW, Locales.ZH_HANT_TW],
  ])
  return countryLocaleMap.get(country) || Locales.KO_KR
}

export const countryCodeToPathnamePrefix = (countryCode: CountryCodes): PathnamePrefix => {
  const countryCodeMap = new Map<CountryCodes, PathnamePrefix>([
    [CountryCodes.KR, ''],
    [CountryCodes.JP, '/jp'],
    [CountryCodes.US, '/us'],
    [CountryCodes.TH, '/th'],
    [CountryCodes.CN, '/cn'],
    [CountryCodes.TW, '/tw'],
  ])
  return countryCodeMap.get(countryCode) || ''
}

export const localeToLanguage = (locale: Locales): Languages => {
  const languageLocaleMap = new Map<Locales, Languages>([
    [Locales.KO_KR, Languages.KO],
    [Locales.JA_JP, Languages.JA],
    [Locales.EN_US, Languages.EN],
    [Locales.TH_TH, Languages.TH],
    [Locales.ZH_HANS_CN, Languages.ZH_HANS],
    [Locales.ZH_HANT_TW, Languages.ZH_HANT],
  ])
  return languageLocaleMap.get(locale) || Languages.KO
}

export const countryToLanguage = (country: Countries): Languages => {
  const locale = countryToLocale(country)
  return localeToLanguage(locale)
}

export const localeToCountryCode = (locale: Locales): CountryCodes => {
  const countryCodeLocaleMap = new Map<Locales, CountryCodes>([
    [Locales.KO_KR, CountryCodes.KR],
    [Locales.JA_JP, CountryCodes.JP],
    [Locales.EN_US, CountryCodes.US],
    [Locales.TH_TH, CountryCodes.TH],
    [Locales.ZH_HANS_CN, CountryCodes.CN],
    [Locales.ZH_HANT_TW, CountryCodes.TW],
  ])
  const countryCode = countryCodeLocaleMap.get(locale) || CountryCodes.KR
  return countryCode || CountryCodes.KR
}

export const localeToCountry = (locale: Locales): Countries => {
  const countryLocaleMap = new Map<Locales, Countries>([
    [Locales.KO_KR, Countries.KR],
    [Locales.JA_JP, Countries.JP],
    [Locales.EN_US, Countries.US],
    [Locales.TH_TH, Countries.TH],
    [Locales.ZH_HANS_CN, Countries.CN],
    [Locales.ZH_HANT_TW, Countries.TW],
  ])
  const country = countryLocaleMap.get(locale)
  return country || Countries.KR
}

export const localeToCountryUrlPrefix = (locale: Locales): PathnamePrefix => {
  const country = localeToCountry(locale)
  return country === Countries.KR ? '' : `/${country}`
}

export const getCountryFromUrl = (pathname: string): Countries => {
  if (pathname.startsWith('/jp')) {
    return Countries.JP
  }
  if (pathname.startsWith('/us')) {
    return Countries.US
  }
  if (pathname.startsWith('/th')) {
    return Countries.TH
  }
  if (pathname.startsWith('/cn')) {
    return Countries.CN
  }
  if (pathname.startsWith('/tw')) {
    return Countries.TW
  }
  // Legacy Chinese locale paths
  if (pathname.startsWith('/zh-hans') || pathname.startsWith('/zh-CN')) {
    return Countries.CN
  }
  if (pathname.startsWith('/zh-hant')) {
    return Countries.TW
  }
  return Countries.KR
}

export const getSearchDistrictCodes = (locale: Locales, options?: { isCrossCountry?: boolean }) => {
  if (locale === Locales.JA_JP && options?.isCrossCountry) {
    return SEARCH_DISTRICT_CODES_FOR_JP_USER_INCLUDING_KO_HOSPITALS
  }
  if (locale === Locales.JA_JP) {
    return SEARCH_DISTRICT_CODES_FOR_JP_USER
  }

  return SEARCH_DISTRICT_CODES_FOR_KO_USER
}

export const extractLocaleFromUrlPath = (urlPath: string): Locales => {
  const supportedCountries = Object.values(Countries)
  const legacySlugs = ['zh-hans', 'zh-hant', 'zh-CN', 'zh-hans-cn', 'zh-hant-tw']
  const allSlugs = [...supportedCountries, ...legacySlugs]
  const pattern = `^\\/(${allSlugs.map((c) => c.replace('-', '\\-')).join('|')})(\\/|\\?|#|$)`
  const match = new RegExp(pattern).exec(urlPath)
  const matched = match ? match[1] : ''
  const slugToCountry = new Map<string, Countries>([
    ['cn', Countries.CN],
    ['zh-hans', Countries.CN],
    ['zh-hans-cn', Countries.CN],
    ['zh-CN', Countries.CN],
    ['tw', Countries.TW],
    ['zh-hant', Countries.TW],
    ['zh-hant-tw', Countries.TW],
    ['jp', Countries.JP],
    ['us', Countries.US],
    ['th', Countries.TH],
    ['kr', Countries.KR],
  ])

  const country = slugToCountry.get(matched) || Countries.KR
  return countryToLocale(country)
}

/**
 * Parse BCP-47 accept-language header to Locales enum
 * @param acceptLanguage - accept-language header value (e.g., "ja-JP,ja;q=0.9,ko;q=0.8")
 * @returns Locales enum
 */
export const parseAcceptLanguageToLocale = (acceptLanguage: string): Locales => {
  if (!acceptLanguage) return Locales.KO_KR

  // Extract the first (most preferred) language
  const primaryLang = acceptLanguage.split(',')[0].trim().toLowerCase()

  // Map BCP-47 language codes to Locales enum
  if (primaryLang.startsWith('ja')) return Locales.JA_JP
  if (primaryLang.startsWith('en')) return Locales.EN_US
  if (primaryLang.startsWith('th')) return Locales.TH_TH
  if (primaryLang.includes('hans') || primaryLang === 'zh-cn') return Locales.ZH_HANS_CN
  if (primaryLang.includes('hant') || primaryLang === 'zh-tw' || primaryLang === 'zh-hk') return Locales.ZH_HANT_TW
  if (primaryLang.startsWith('ko')) return Locales.KO_KR

  return Locales.EN_US // fallback to default
}
